import type { Client, Contact, Program } from '@efdigital/ll-addon-data';
import {
  setAsyncBelongsTo,
  setAsyncHasMany,
} from '@efdigital/ll-admin-v2/data/async-setter';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import {
  EditRecord,
  type EditRecordSignature,
  initialState,
} from '../edit-record.gts';
import ClientPicker from '../pickers/client-picker.gts';
import { ContactPicker } from '../pickers/contact-picker.gts';
import { Validation } from '../validation.gts';

function formatContactName(contact: Contact): string {
  return [contact.title, contact.firstname, contact.lastname, contact.suffix]
    .filter(Boolean)
    .join(' ');
}

export class EditProgram extends Component<EditRecordSignature<Program>> {
  @service declare store: Store;

  handleClientSelect = (client: Client | null) => {
    setAsyncBelongsTo(this.args.item, 'client', client);
  };

  handleContactSelect = async (contact: Contact) => {
    const contacts = await this.args.item.contacts;
    if (!contacts.find((item) => item.id === contact.id)) {
      setAsyncHasMany(this.args.item, 'contacts', [...contacts, contact]);
    }
  };

  removeContact = async (contact: Contact) => {
    const contacts = await this.args.item.contacts;
    setAsyncHasMany(
      this.args.item,
      'contacts',
      contacts.filter((c) => c.id !== contact.id)
    );
  };

  setTitle = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.title = target.value;
  };

  validate = (item: Program) => {
    if (!item.client?.id) {
      return Validation.fromFailures([
        {
          message: 'Client is required',
          attribute: 'client',
        },
      ]);
    }

    return Validation.success();
  };

  <template>
    <EditRecord
      @editLabel="Edit Program"
      @item={{@item}}
      @context={{@context}}
      @buttonSize="btn-md"
      @initialState={{initialState @item}}
      @validate={{this.validate}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Details</legend>

          <div class="mb-3">
            <label for="{{uniqueId}}-title" class="form-control-label required">
              Program Name
            </label>
            <input
              name="title"
              type="text"
              class="form-control"
              id="{{uniqueId}}-title"
              value={{@item.title}}
              required
              placeholder="Program Name"
              {{on "change" this.setTitle}}
            />
          </div>

          <div class="mb-3">
            <Await @promise={{@item.client}}>
              <:success as |client|>
                <ClientPicker
                  @client={{client}}
                  @onSelect={{this.handleClientSelect}}
                  @readonly={{isReadOnly}}
                  @allowPublic={{false}}
                  @label="Client"
                />
              </:success>
            </Await>
          </div>
        </fieldset>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Faculty</legend>

          <Await @promise={{@item.contacts}}>
            <:success as |contacts|>
              {{#if contacts.length}}
                <div class="list-group">
                  {{#each contacts as |contact|}}
                    <div class="list-group-item">
                      <div
                        class="d-flex justify-content-start align-items-top gap-3"
                      >
                        {{#if contact.icon}}
                          <div class="flex-shrink-0 flex-grow-0">
                            <div class="ratio ratio-1x1 w-48px">
                              <img
                                src={{contact.icon}}
                                class="object-fit-cover rounded-circle"
                                alt={{formatContactName contact}}
                                width="48"
                              />
                            </div>
                          </div>
                        {{/if}}
                        <div class="flex-grow-1">
                          <h4 class="h5 mb-1">
                            {{formatContactName contact}}
                          </h4>
                          {{#if contact.organisation}}
                            <p class="list-group-item-text mb-0 small">
                              <iconify-icon icon="ph:building"></iconify-icon>
                              {{contact.organisation}}
                            </p>
                          {{/if}}
                          {{#if contact.email}}
                            <p class="list-group-item-text mb-0 small">
                              <iconify-icon icon="ph:envelope"></iconify-icon>
                              <a
                                href="mailto:{{contact.email}}"
                              >{{contact.email}}</a>
                            </p>
                          {{/if}}
                        </div>
                        <div class="flex-shrink-0 flex-grow-0">
                          {{#unless isReadOnly}}
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-danger"
                              {{on "click" (fn this.removeContact contact)}}
                            >
                              <iconify-icon
                                inline
                                icon="ph:x-bold"
                              ></iconify-icon>
                              <span class="visually-hidden">Remove</span>
                            </button>
                          {{/unless}}
                        </div>
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{else}}
                <p>No faculty assigned yet.</p>
              {{/if}}
              {{#unless isReadOnly}}
                <ContactPicker @onSelect={{this.handleContactSelect}}>
                  <:input as |showModal|>
                    <button
                      type="button"
                      class="btn btn-secondary btn-sm mt-3"
                      {{on "click" showModal}}
                    >
                      <iconify-icon inline icon="ph:plus-bold"></iconify-icon>
                      Add Faculty
                    </button>
                  </:input>
                </ContactPicker>
              {{/unless}}
            </:success>
          </Await>
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}
